
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Camera, Users, Info } from "lucide-react";
import { UserProfile } from "@/types/user";
import { Button } from "@/components/ui/button";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ChatType } from "./types";

type ChatHeaderProps = {
  recipient: UserProfile;
  onOpenProfilePicDialog: () => void;
  chatType?: ChatType;
  members?: UserProfile[];
  groupName?: string;
};

export default function ChatHeader({ 
  recipient, 
  onOpenProfilePicDialog,
  chatType = 'individual',
  members = [],
  groupName
}: ChatHeaderProps) {
  const isGroup = chatType === 'group';
  const recipientAvatar = recipient.avatarUrl || `https://api.dicebear.com/7.x/personas/svg?seed=${recipient.name}`;
  
  return (
    <div className="flex items-center justify-between p-4 bg-card">
      <div className="flex items-center gap-3">
        {isGroup ? (
          <Avatar className="cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all">
            <div className="bg-primary/10 h-full w-full flex items-center justify-center">
              <Users className="h-4 w-4 text-primary" />
            </div>
            <AvatarFallback>{groupName?.slice(0, 2).toUpperCase() || "GC"}</AvatarFallback>
          </Avatar>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Avatar className="cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all">
                <AvatarImage src={recipientAvatar} />
                <AvatarFallback>{recipient?.name?.slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={onOpenProfilePicDialog}>
                <Camera className="mr-2 h-4 w-4" />
                <span>Change Profile Picture</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <div>
          <h3 className="font-medium">{isGroup ? groupName : recipient?.name}</h3>
          <p className="text-sm text-muted-foreground">
            {isGroup ? `${members.length} members` : recipient?.teamType}
          </p>
        </div>
      </div>
      
      {isGroup && members.length > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon">
                <Info className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="end" className="w-80 p-0">
              <div className="p-4">
                <h4 className="font-medium mb-2">Group Members</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {members.map(member => (
                    <div key={member.id} className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={member.avatarUrl} />
                        <AvatarFallback>{member.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{member.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
